import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  Building2,
  Home,
  Kanban,
  ListTodo,
  MoreHorizontal,
  Plus,
  Settings,
  User,
  Users,
} from "lucide-react";
import { useTranslate } from "ra-core";
import {
  Link,
  matchPath,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";

const ORANGE = "var(--nd-orange)";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();
  const [moreOpen, setMoreOpen] = useState(false);

  let currentPath: string | false = false;
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (
    matchPath("/tasks/*", location.pathname) ||
    matchPath("/settings", location.pathname)
  ) {
    currentPath = "more";
  }

  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <>
      {/* Bottom nav */}
      <nav
        aria-label={translate("crm.navigation.label")}
        className="fixed bottom-0 left-0 right-0 z-50 h-14"
        style={{
          background: "var(--nd-surface)",
          borderTop: "1px solid var(--nd-border)",
          paddingBottom: isPwa && isWebiOS ? 15 : undefined,
          height: isPwa && isWebiOS ? "calc(3.5rem + 15px)" : undefined,
        }}
      >
        <div className="flex items-center h-full px-2">
          <NavigationButton
            href="/"
            Icon={Home}
            label={translate("ra.page.dashboard")}
            isActive={currentPath === "/"}
          />
          <NavigationButton
            href="/contacts"
            Icon={Users}
            label={translate("resources.contacts.name", { smart_count: 2 })}
            isActive={currentPath === "/contacts"}
          />
          <NavigationButton
            href="/companies"
            Icon={Building2}
            label={translate("resources.companies.name", { smart_count: 2 })}
            isActive={currentPath === "/companies"}
          />
          <NavigationButton
            href="/deals"
            Icon={Kanban}
            label={translate("crm.menu.deals", { _: "Pipeline" })}
            isActive={currentPath === "/deals"}
          />
          <MoreButton
            open={moreOpen}
            onOpenChange={setMoreOpen}
            translate={translate}
            isActive={currentPath === "more"}
          />
        </div>
      </nav>

      {/* FAB — floats above nav bar, bottom right */}
      <CreateButton />
    </>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-1 h-auto py-2 px-1 rounded-none flex-1 min-w-0",
    )}
    style={{
      color: isActive ? "var(--nd-text-display)" : "var(--nd-text-disabled)",
    }}
  >
    <Link to={href} aria-current={isActive ? "page" : undefined}>
      <Icon className="size-5 shrink-0" strokeWidth={isActive ? 2 : 1.5} />
      <span className="font-nd-mono text-[7px] tracking-[0.06em] uppercase w-full truncate text-center">
        {label}
      </span>
    </Link>
  </Button>
);

const MoreButton = ({
  open,
  onOpenChange,
  translate,
  isActive,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  translate: (key: string, options?: any) => string;
  isActive: boolean;
}) => {
  return (
    <>
      <Button
        variant="ghost"
        className={cn(
          "flex-col gap-1 h-auto py-2 px-1 rounded-none flex-1 min-w-0",
        )}
        style={{
          color:
            isActive || open
              ? "var(--nd-text-display)"
              : "var(--nd-text-disabled)",
        }}
        aria-expanded={open}
        onClick={() => onOpenChange(true)}
      >
        <MoreHorizontal
          className="size-5 shrink-0"
          strokeWidth={isActive || open ? 2 : 1.5}
        />
        <span className="font-nd-mono text-[7px] tracking-[0.06em] uppercase w-full truncate text-center">
          {translate("crm.navigation.more", { _: "More" })}
        </span>
      </Button>

      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="pb-safe rounded-t-2xl">
          <SheetHeader className="sr-only">
            <SheetTitle>
              {translate("crm.navigation.more", { _: "More" })}
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col gap-1 pt-2 pb-4">
            <SheetNavItem
              href="/tasks"
              Icon={ListTodo}
              label={translate("resources.tasks.name", { smart_count: 2 })}
              onClick={() => onOpenChange(false)}
            />
            <SheetNavItem
              href="/settings"
              Icon={User}
              label={translate("crm.profile.title", { _: "Profile" })}
              onClick={() => onOpenChange(false)}
            />
            <SheetNavItem
              href="/settings"
              Icon={Settings}
              label={translate("crm.settings.title", { _: "Settings" })}
              onClick={() => onOpenChange(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const SheetNavItem = ({
  href,
  Icon,
  label,
  onClick,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  onClick: () => void;
}) => (
  <Link
    to={href}
    onClick={onClick}
    className="flex items-center gap-3 px-4 py-3 rounded-lg no-underline transition-colors"
    style={{ color: "var(--nd-text-primary)" }}
  >
    <Icon
      className="size-5"
      strokeWidth={1.5}
      style={{ color: "var(--nd-text-secondary)" }}
    />
    <span className="text-base">{label}</span>
  </Link>
);

const CreateButton = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="fixed bottom-20 right-4 z-50 h-12 w-12 rounded-full shadow-lg hover:opacity-90 transition-opacity"
            style={{ background: ORANGE, color: "white" }}
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-5" strokeWidth={2.5} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end">
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setContactCreateOpen(true)}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setNoteCreateOpen(true)}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => setTaskCreateOpen(true)}
          >
            {translate("resources.tasks.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => navigate("/deals/create")}
          >
            {translate("resources.deals.forcedCaseName", { _: "Deal" })}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
